import type { StoredGroup } from '~/gql/groups/groups'
import type { EditableGroup } from '~/pages/backoffice/admin/groups/components/GroupModal'
import { useQuery } from '@apollo/client/react'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader } from '~/components/ui/card'
import { GQL_GROUPS } from '~/gql/groups/groups'

import { GQL_USERS_WITH_FILTERS } from '~/gql/users/users'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useListSearch } from '~/hooks/use-list-search'
import GroupDeleteModal from '~/pages/backoffice/admin/groups/components/GroupDeleteModal'
import GroupModal from '~/pages/backoffice/admin/groups/components/GroupModal'
import GroupsList from '~/pages/backoffice/admin/groups/components/GroupsList'
import { filterGroups } from '~/pages/backoffice/admin/groups/filter-groups'
import { NoResults } from '~/pages/backoffice/NoResults'
import ResultsStatus from '~/pages/backoffice/ResultsStatus'
import TablePagination from '~/pages/backoffice/TablePagination'
import { SearchInput, TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'

/**
 * Gestion des groupes d'une organisation (#135) : créer, renommer, supprimer, et
 * composer les groupes. Vit à côté de la page Utilisateurs, dont elle est la
 * maille supérieure.
 */
function GroupsTable() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_GROUPS, { fetchPolicy: 'cache-and-network' })
  const { data: usersData } = useQuery(GQL_USERS_WITH_FILTERS, {
    variables: { input: { skip: 0, take: 1000, searchInput: '' } },
  })

  const [openModal, setOpenModal] = useState(false)
  const [editing, setEditing] = useState<EditableGroup | undefined>()
  const [deleting, setDeleting] = useState<StoredGroup | undefined>()
  const { search, setSearch, query } = useListSearch()

  const groups = useMemo(() => data?.groups ?? [], [data])
  const visibleGroups = useMemo(() => filterGroups(groups, query), [groups, query])
  const { pageRows, pagination } = useClientPagination(visibleGroups, query)
  const users = usersData?.getFilteredUsers?.users ?? []

  const openCreate = () => {
    setEditing(undefined)
    setOpenModal(true)
  }

  const openEdit = (group: StoredGroup) => {
    setEditing({ id: group.id, name: group.name, description: group.description })
    setOpenModal(true)
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <Card {...tourAnchor('groups.list')}>
        {/* Le titre de l'écran est porté par le layout (`BackOfficeLayout`) :
            le répéter ici ne faisait qu'afficher deux fois le même mot. */}
        <CardHeader>
          <TableToolbar>
            <SearchInput
              {...tourAnchor('groups.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <ToolbarActions>
              <Button onClick={openCreate} {...tourAnchor('groups.new')}>
                <Plus className="mr-2 size-4" />
                {t('groups.new')}
              </Button>
            </ToolbarActions>
          </TableToolbar>
          <ResultsStatus count={visibleGroups.length} filtering={query !== ''} />
        </CardHeader>
        <CardContent>
          {loading && groups.length === 0
            ? <div className="flex justify-center p-6"><Spinner className="size-6" /></div>
            : groups.length === 0
              ? <p className="p-4 text-center text-sm text-muted-foreground">{t('groups.empty')}</p>
              : visibleGroups.length === 0
                ? <NoResults query={query} onReset={() => setSearch('')} />
                : (
                    <>
                      <GroupsList groups={pageRows} onEdit={openEdit} onDelete={setDeleting} />
                      <TablePagination {...pagination} />
                    </>
                  )}
        </CardContent>
      </Card>

      {openModal && (
        <GroupModal
          open={openModal}
          onCancel={() => { setOpenModal(false); setEditing(undefined) }}
          onSuccess={refetch}
          users={users}
          group={editing}
        />
      )}

      {deleting && (
        <GroupDeleteModal
          open={!!deleting}
          onCancel={() => setDeleting(undefined)}
          onSuccess={refetch}
          group={deleting}
        />
      )}
    </div>
  )
}

export default GroupsTable
